import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData, fetchData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const IncomeCreate = () => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        BillDate: '',
        BillNo: 0,
        ExpenseHeadId: '',
        Amount: 0,
        Remarks: '',
        createdBy: userId,
        IsIncome: true,
    });

    const handleCustomerSelect = (selectedOption) => {
        setSelectedCustomer(selectedOption);
        setFormData((prevData) => ({
            ...prevData,
            ExpenseHeadId: selectedOption ? selectedOption.value : '',
            CreatedBy: userId,
        }));
    };

    useEffect(() => {
        fetchCustomerData();
    }, []);

    const fetchCustomerData = () => {
        fetchData('IncomeApi', `/AccountHead/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.ID,
                }));
                setCustomerOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching Account Head data:', error);
            });
    };

    const submitData = async () => {
        setIsLoading(true);
        setErrorMessage('');

        if (!formData.ExpenseHeadId || formData.ExpenseHeadId === 0) {
            setErrorMessage('Account Head is required!');
            setIsLoading(false);
            return;
        }

        if (!formData.Amount || formData.Amount === 0) {
            setErrorMessage('Amount must be greater than 0!');
            setIsLoading(false);
            return;
        }

        const createData = {
            ...formData,
            CreatedBy: userId,
        };

        postData('IncomeApi', createData, token)
            .then(() => {
                setSuccessMessage('Income created successfully!');
                history.push('/IncomeIndex');
            })
            .catch((error) => {
                console.error('Income Create error:', error);
                setErrorMessage('Failed to create income.');
                setIsLoading(false);
            });
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        setFormData((prevData) => ({
            ...prevData,
            BillDate: `${year}-${month}-${day}`,
        }));
    }, []);

    return (
        <Fragment>
            <div className="form-container">
                <div className="form-card">
                    {/* Header */}
                    <div className="form-header">
                        <h1 className="form-title">Create Income</h1>
                        <div className="form-actions">
                            <Link to="/IncomeIndex" className="btn-back">
                                <ArrowBackIcon fontSize="small" />
                                Back to List
                            </Link>
                            <button className="btn-save" onClick={submitData} disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <>
                                        <SaveIcon fontSize="small" />
                                        Save
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    {errorMessage && <div className="message error">{errorMessage}</div>}
                    {successMessage && <div className="message success">{successMessage}</div>}

                    {/* Form Body */}
                    <div className="form-body">
                        {/* Row 1 */}
                        <div className="form-row">
                            <div className="form-group form-select" style={{ flex: 2 }}>
                                <label className="form-label">Account Head *</label>
                                <Select
                                    options={customerOptions}
                                    value={selectedCustomer}
                                    onChange={handleCustomerSelect}
                                    placeholder="Select account head"
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Date *</label>
                                <input
                                    className="form-input"
                                    value={formatDateForInput(formData.BillDate)}
                                    type="date"
                                    name="BillDate"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill No</label>
                                <input
                                    className="form-input"
                                    value={formData.BillNo}
                                    type="text"
                                    name="BillNo"
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Remarks</label>
                                <input
                                    className="form-input"
                                    value={formData.Remarks || ''}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter remarks"
                                    name="Remarks"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Amount *</label>
                                <input
                                    className="form-input"
                                    value={formData.Amount || ''}
                                    type="number"
                                    placeholder="0.00"
                                    onChange={handleChange}
                                    name="Amount"
                                    style={{ textAlign: 'right' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default IncomeCreate;
